/*
  Admin › Access (28) — wave 1: ideas 2 (calm cells: a navy tick on a soft square vs a faint dash, and
  "N/11" under every module), 3 (role column: one-line name + "N users" + Default / Custom; Super Admin
  is one merged "All 18 modules — always" cell), 5 (hidden rules in the header: Shop Drawings "via Tasks",
  AI locked to Super Admin by default; plain intro + legend) and 7 (Copy from… / Reset on row hover).
  Wave 2: ideas 1 (every module on one screen under a two-row header: "App pages" | "Inside a project";
  sticky header and role column), 4 (a tick is a draft — amber cell — until "Review & apply" saves every
  changed role at once, as one history entry the toast can undo; leaving with a draft asks first) and
  9 (phones: one accordion per role with 44px switches, split App pages / Inside a project).
  Wave 3: ideas 6 ("Preview as role" under the matrix: the sidebar and project tabs a role gets with the draft,
  what is hidden, who it affects) and 8 (View / Edit levels: "View · Edit" mode — a click cycles a cell
  Edit → View → none → Edit; existing access stays Edit). "View only" is offered only for the modules whose
  writes the server refuses for a view-only role today (GET /api/admin/roles `level_modules`); the others
  (`level_modules_soon`) stay on / off in that mode, and a line under the toolbar says so. Phones have no mode
  switch: their accordion is always on / off and marks view-only modules in words.
  Final QA: the role column is 236px at every width; "Copy from…" and "Reset" (in words) replace the "N users" line
  while a row is hovered or focused, so they never squeeze the role name. The View · Edit note names the pages that
  hide their edit buttons from a view-only role (lib/permissions EDITABLE_PAGES) and those that still show them.
*/
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Copy, Eye, Link2, Lock, LogOut, Pencil, RotateCcw, ShieldCheck } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { PERMISSION_MODULES, SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { useIsPhone } from '@/hooks/useMediaQuery';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusPill } from '@/components/ui/status-pill';
import { UnsavedBar } from '@/components/ui/unsaved-bar';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { Segmented } from '@/components/admin/AdminUi';
import RolePreview from '@/components/admin/RolePreview';
import {
  LEVEL_MODULE_KEYS, READ_ONLY_ROLES, accessAndLevelChanges, accessChangeCount, accessChangeText, accessLevel,
  accessModuleGroups, editButtonsNote, effectiveViewOnly, moduleAccessCounts, nextAccessLevel, setAccessDraft,
  setLevelDraft, toggleAccessDraft,
} from '@/components/admin/adminModel';

const MODE_KEY = storageKey('admin', 'access', 'mode');
const MODE_OPTIONS = [
  { value: 'onoff', label: 'On / Off' },
  { value: 'levels', label: 'View · Edit' },
];

// Column headers are ~52px wide: break long names at a sensible place (soft hyphens), never mid-letter.
const HEADER_LABELS = {
  dashboard: 'Dash­board',
  shop_drawings: 'Shop Drawings',
  inventory: 'Inven­tory',
  procurement: 'Procure­ment',
  forecast: 'Fore­cast',
  invoicing: 'Invoic­ing',
  project_structure: 'Project Struc­ture',
  execution: 'Execu­tion',
  timeline: 'Time­line',
  variations: 'Varia­tions',
  material: 'Mate­rial',
  stage_lpo_loi: 'LPO/​LOI stage',
};

const HINTS = {
  shop_drawings: 'Anyone with Tasks can also open Shop Drawings.',
  ai: 'Super Admin only by default.',
  stage_lpo_loi: 'The LPO/LOI stage on project pages. Admin and Presales by default.',
};

/** Tick / dash / draft — never colour alone: the draft square has an amber frame AND the screen-reader label. */
function AccessMark({ on, draft = false }) {
  if (draft) {
    return (
      <span className="inline-grid h-6 w-6 place-items-center rounded-[7px] border-[1.5px] border-[color:var(--status-amber)] bg-status-amber-bg text-brand-ink" data-draft="true">
        {on ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" /> : <span className="inline-block h-[1.5px] w-2.5 rounded bg-[color:var(--status-amber-ink)]" aria-hidden="true" />}
      </span>
    );
  }
  return on ? (
    <span className="inline-grid h-[18px] w-[18px] place-items-center rounded-[5px] bg-brand-soft text-brand-ink">
      <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
    </span>
  ) : (
    <span className="inline-block h-[1.5px] w-2.5 rounded bg-[color:var(--ink-4)]" aria-hidden="true" />
  );
}

/** View · Edit mode: "Edit" (navy) / "View" (grey, eye) — words, not colour alone; amber frame while a draft. */
function LevelMark({ level, draft = false, fixed = false }) {
  if (!level) return <AccessMark on={false} draft={draft} />;
  const Icon = level === 'view' ? Eye : Pencil;
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center gap-1 rounded-md px-1.5 text-[10.5px] font-bold',
        level === 'view' ? 'bg-[color:var(--status-gray-bg)] text-ink-2' : 'bg-brand-soft text-brand-ink',
        draft && 'ring-[1.5px] ring-[color:var(--status-amber)]',
        fixed && 'opacity-80'
      )}
      data-level={level}
    >
      <Icon className="h-[11px] w-[11px]" aria-hidden="true" />
      {level === 'view' ? 'View' : 'Edit'}
    </span>
  );
}

function LevelsLegend() {
  return (
    <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12px] text-ink-2" aria-label="Legend" data-testid="access-levels-legend">
      <li className="inline-flex items-center gap-1.5"><LevelMark level="edit" /> can change</li>
      <li className="inline-flex items-center gap-1.5"><LevelMark level="view" /> can only look</li>
      <li className="inline-flex items-center gap-1.5"><span className="inline-grid h-[18px] w-[18px] place-items-center"><AccessMark on={false} /></span> No access</li>
      <li className="inline-flex items-center gap-1.5"><span className="scale-[.8]"><AccessMark on draft /></span> Draft</li>
    </ul>
  );
}

function Legend() {
  return (
    <ul className="flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12px] text-ink-2" aria-label="Legend">
      <li className="inline-flex items-center gap-1.5"><AccessMark on /> Has access</li>
      <li className="inline-flex items-center gap-1.5"><span className="inline-grid h-[18px] w-[18px] place-items-center"><AccessMark on={false} /></span> No access</li>
      <li className="inline-flex items-center gap-1.5"><span className="scale-[.8]"><AccessMark on draft /></span> Changed (draft)</li>
      <li className="inline-flex items-center gap-1.5"><Lock className="h-[13px] w-[13px] text-ink-3" aria-hidden="true" /> Fixed</li>
      <li className="inline-flex items-center gap-1.5">
        <span className="rounded-md border border-[color:var(--brand-soft-border)] bg-brand-soft px-1.5 text-[11px] font-semibold text-brand-ink">Custom</span>
        edited, not default
      </li>
    </ul>
  );
}

// While a row is hovered / focused its actions take the role cell's second line, full width: the tag steps aside.
function RoleTag({ draft, custom, canManage, role }) {
  if (draft) return <StatusPill tone="amber" label="Draft" size="sm" className={cn('shrink-0', canManage && 'group-hover:hidden group-focus-within:hidden')} data-testid={`access-tag-${role}`} />;
  return (
    <span
      className={cn(
        'shrink-0 rounded-md border px-1.5 text-[11px] font-semibold',
        canManage && 'group-hover:hidden group-focus-within:hidden',
        custom ? 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink' : 'border-line bg-surface text-ink-3'
      )}
      data-testid={`access-tag-${role}`}
    >
      {custom ? 'Custom' : 'Default'}
    </span>
  );
}

export default function AccessTab({ roleAccess, users = [], refreshRoles, onDirtyChange }) {
  const confirm = useConfirm();
  const isPhone = useIsPhone();
  const [draft, setDraft] = useState({});
  const [levelDraft, setLevelDraftState] = useState({});
  const [mode, setModeState] = useState(() => (readStored(MODE_KEY, 'onoff') === 'levels' ? 'levels' : 'onoff'));
  const [previewRole, setPreviewRole] = useState('');
  const previewRef = useRef(null);
  // below 1440px the matrix scrolls inside its card: a fade on the right says more columns are there
  const matrixScrollRef = useRef(null);
  const [moreRight, setMoreRight] = useState(false);
  const measureMatrix = useCallback(() => {
    const node = matrixScrollRef.current;
    setMoreRight(Boolean(node) && node.scrollLeft + node.clientWidth < node.scrollWidth - 2);
  }, []);
  useLayoutEffect(() => {
    const node = matrixScrollRef.current;
    if (!node) return undefined;
    measureMatrix();
    node.addEventListener('scroll', measureMatrix, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measureMatrix) : null;
    observer?.observe(node);
    return () => { node.removeEventListener('scroll', measureMatrix); observer?.disconnect(); };
  });
  const [status, setStatus] = useState('idle');
  const [applyError, setApplyError] = useState('');
  const [openRole, setOpenRole] = useState('');
  const roles = useMemo(() => roleAccess.roles || [], [roleAccess.roles]);
  const saved = useMemo(() => roleAccess.permissions || {}, [roleAccess.permissions]);
  const savedView = useMemo(() => roleAccess.view_only || {}, [roleAccess.view_only]);
  // where "View only" can be set (the server enforces it there) — none unless the server says
  const levelModules = useMemo(() => new Set(roleAccess.level_modules || []), [roleAccess.level_modules]);
  // read-only roles are View in every module that has changes to make (auth refuses all their writes)
  const allLevelModules = useMemo(
    () => new Set(roleAccess.level_modules_soon ? [...(roleAccess.level_modules || []), ...roleAccess.level_modules_soon] : LEVEL_MODULE_KEYS),
    [roleAccess.level_modules, roleAccess.level_modules_soon],
  );
  const readOnlyRoles = useMemo(() => new Set(roleAccess.read_only_roles || READ_ONLY_ROLES), [roleAccess.read_only_roles]);
  // phones show no mode switch, so a View · Edit mode saved on a desktop never applies there
  const levelsOn = mode === 'levels' && !isPhone;
  const setMode = (value) => { setModeState(value); writeStored(MODE_KEY, value); };
  const details = roleAccess.role_details || {};
  const canManage = Boolean(roleAccess.can_manage_permissions);
  const modules = PERMISSION_MODULES;
  const groups = useMemo(() => accessModuleGroups(modules), [modules]);
  const ordered = useMemo(() => groups.flatMap((group) => group.modules), [groups]);
  const firstProjectKey = groups[1]?.modules[0]?.key;

  const effective = useMemo(() => ({ ...saved, ...draft }), [saved, draft]);
  const counts = useMemo(() => moduleAccessCounts(roles, effective, modules), [roles, effective, modules]);
  // the view-only modules each role ends up with (read-only roles: everything they open)
  const viewOf = useMemo(() => {
    const result = {};
    roles.forEach((role) => {
      const permissions = effective[role] || [];
      result[role] = readOnlyRoles.has(role)
        ? permissions.filter((key) => allLevelModules.has(key))
        : effectiveViewOnly(role, permissions, levelDraft, savedView);
    });
    return result;
  }, [roles, effective, levelDraft, savedView, readOnlyRoles, allLevelModules]);
  const savedViewOf = (role) => (readOnlyRoles.has(role)
    ? (saved[role] || []).filter((key) => allLevelModules.has(key))
    : (savedView[role] || []));
  const changes = useMemo(
    () => accessAndLevelChanges({ draft, levelDraft, saved, savedView, roles }),
    [draft, levelDraft, saved, savedView, roles],
  );
  // Preview as role: the role the admin picked, else the first role in the draft, else Site Engineer
  const shownPreviewRole = previewRole || changes[0]?.role || (roles.includes('Site Engineer') ? 'Site Engineer' : roles[0] || '');
  const changeCount = accessChangeCount(changes);
  // the modules a draft touches (their header counts turn amber)
  const draftModules = useMemo(() => new Set(changes.flatMap((change) => [
    ...change.added, ...change.removed, ...(change.toView || []), ...(change.toEdit || []),
  ])), [changes]);
  const totalRoles = roles.length;
  const customCount = roles.filter((role) => details[role]?.is_custom).length;

  useEffect(() => { onDirtyChange?.(changeCount); }, [changeCount, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(0), [onDirtyChange]);

  // The people list is kept current by the Users tab; the server's count is the fallback before it loads.
  const userCount = (role) => {
    if (users.length) return users.filter((user) => user.role === role).length;
    return Number.isFinite(details[role]?.user_count) ? details[role].user_count : 0;
  };
  const peopleText = (role) => {
    const count = userCount(role);
    if (role === SUPER_ADMIN_ROLE && count === 1) {
      const person = users.find((user) => user.role === SUPER_ADMIN_ROLE);
      return person ? `1 user · ${person.name}` : '1 user';
    }
    return `${count} ${count === 1 ? 'user' : 'users'}`;
  };
  const affected = changes.reduce((sum, change) => sum + userCount(change.role), 0);

  const edit = (next, nextLevels = levelDraft) => {
    setStatus((current) => (current === 'saving' ? current : 'idle'));
    setApplyError('');
    setDraft(next);
    setLevelDraftState(nextLevels);
  };
  // levels of one role in the draft (read-only roles keep none: they are View by rule)
  const withLevels = (levels, role, viewOnly) => (readOnlyRoles.has(role) ? levels : setLevelDraft(levels, savedView, role, viewOnly));
  const toggle = (role, moduleKey) => {
    if (!canManage || role === SUPER_ADMIN_ROLE || status === 'saving') return;
    if (!levelsOn || !levelModules.has(moduleKey) || readOnlyRoles.has(role)) {
      const nextDraft = toggleAccessDraft(draft, saved, role, moduleKey);
      const permissions = nextDraft[role] || saved[role] || [];
      edit(nextDraft, withLevels(levelDraft, role, viewOf[role].filter((key) => permissions.includes(key))));
      return;
    }
    // View · Edit: Edit → View → none → Edit
    const permissions = effective[role] || [];
    const view = viewOf[role] || [];
    const next = nextAccessLevel(accessLevel(permissions, view, moduleKey));
    const withModule = permissions.includes(moduleKey) ? permissions : [...permissions, moduleKey];
    let nextPermissions = withModule; // 'edit' (from no access too) and 'view' both open the module
    let nextView = view.filter((key) => key !== moduleKey);
    if (next === null) nextPermissions = permissions.filter((key) => key !== moduleKey);
    else if (next === 'view') nextView = [...nextView, moduleKey];
    edit(setAccessDraft(draft, saved, role, nextPermissions), withLevels(levelDraft, role, nextView));
  };
  const copyFrom = (role, source) => {
    const permissions = effective[source] || [];
    const view = readOnlyRoles.has(source) ? [] : viewOf[source] || [];
    const sameAs = (a = [], b = []) => a.length === b.length && [...a].sort().every((key, index) => key === [...b].sort()[index]);
    const ownView = readOnlyRoles.has(role) ? [] : viewOf[role] || [];
    if (sameAs(effective[role] || [], permissions) && (readOnlyRoles.has(role) || sameAs(ownView, view))) {
      notify.info(`${role} already has the same access as ${source}`, { description: 'Nothing to copy.' });
      return;
    }
    edit(setAccessDraft(draft, saved, role, permissions), withLevels(levelDraft, role, view));
    notify.info(`${role} now has the same access as ${source} — in the draft`, { description: 'Nothing changes for anyone until you apply.' });
  };
  const defaultsOf = (role) => details[role]?.default_permissions;
  const reset = (role) => {
    const defaults = defaultsOf(role);
    if (!defaults) return;
    edit(setAccessDraft(draft, saved, role, defaults), withLevels(levelDraft, role, [])); // defaults are all Edit
  };
  const resettable = roles.filter((role) => role !== SUPER_ADMIN_ROLE && defaultsOf(role)
    && (details[role]?.is_custom || draft[role] || levelDraft[role]));
  const resetAll = () => {
    let next = { ...draft };
    let nextLevels = { ...levelDraft };
    resettable.forEach((role) => {
      next = setAccessDraft(next, saved, role, defaultsOf(role));
      nextLevels = withLevels(nextLevels, role, []);
    });
    edit(next, nextLevels);
  };

  const discard = () => {
    setDraft({});
    setLevelDraftState({});
    setStatus('idle');
    setApplyError('');
  };

  const apply = async () => {
    if (!changes.length) return;
    let result = null;
    const ok = await confirm({
      title: `Apply ${changeCount} access ${changeCount === 1 ? 'change' : 'changes'}?`,
      subtitle: `Affects ${affected} ${affected === 1 ? 'person' : 'people'} · takes effect at their next page load`,
      description: 'What each role gains (+) or loses (−), and what becomes view only:',
      consequences: changes.map(accessChangeText),
      confirmLabel: 'Apply changes',
      icon: ShieldCheck,
      onConfirm: async () => {
        setStatus('saving');
        try {
          const res = await api.put('/admin/roles/permissions', {
            changes: changes.map((change) => ({ role: change.role, permissions: change.permissions, view_only: change.view_only })),
          });
          result = res.data || {};
        } catch (err) {
          setStatus('error');
          setApplyError(getErrorMessage(err, 'Could not apply the access changes'));
          throw err;
        }
      },
    });
    if (!ok) {
      setStatus((current) => (current === 'saving' ? 'idle' : current));
      return;
    }
    const applied = changes;
    await refreshRoles().catch(() => {});
    setDraft({});
    setLevelDraftState({});
    setStatus('saved');
    const undoId = result?.undo_id;
    const names = applied.map((change) => change.role);
    notify.success(`Access updated for ${names.length === 1 ? names[0] : `${names.length} roles`}`, {
      description: applied.map(accessChangeText).join(' · '),
      ...(undoId ? {
        undo: async () => {
          await api.post(`/undo/entries/${undoId}`);
          await refreshRoles();
        },
        undoneMessage: 'Access changes undone',
      } : {}),
    });
  };

  const cellLabel = (role, module, on, isDraft, viaTasks, level, sayViewOnly = false) => {
    let state = 'no access';
    if (on) state = (levelsOn && level) ? (level === 'view' ? 'can view only' : 'can view and edit') : 'has access';
    if (on && sayViewOnly && level === 'view') state = 'has access, view only';
    return `${role} · ${module.label}: ${state}${isDraft ? ' (changed, not applied yet)' : ''}${viaTasks ? ' (included with Tasks)' : ''}`;
  };
  const levelNames = PERMISSION_MODULES.filter((module) => levelModules.has(module.key)).map((module) => module.label);
  const soonCount = (roleAccess.level_modules_soon || []).length;
  const soonNote = !levelNames.length
    ? 'View only is not available for any module yet — every module stays on / off.'
    : soonCount
      ? `View only works for ${levelNames.join(', ')} so far. The other modules stay on / off until the server checks their changes for view-only roles.`
      : `View only works for every module with changes to make: the server refuses every change a view-only role tries, and they see a “view only” message. ${editButtonsNote()}`;

  // Shown on the role's second line while the row is hovered or focused (in place of "N users"), so the role name
  // keeps the whole column; "Copy from…" and "Reset" fit in words at every width.
  const rowActions = (role) => (canManage ? (
    <div className="hidden min-w-0 items-center gap-1 pt-0.5 group-hover:flex group-focus-within:flex" data-testid={`role-actions-${role}`}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="xs" disabled={status === 'saving'} title={`Copy another role's ticks to ${role}`} aria-label={`Copy access from another role to ${role}`} className="h-[22px]" data-testid={`role-copy-${role}`}>
            <Copy aria-hidden="true" /> Copy from…
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[220px] rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
          <DropdownMenuLabel className="px-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Copy access from</DropdownMenuLabel>
          {roles.filter((other) => other !== role && other !== SUPER_ADMIN_ROLE).map((other) => (
            <DropdownMenuItem key={other} className="h-[34px] rounded-lg text-[13px]" onSelect={() => copyFrom(role, other)}>
              {other}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        type="button"
        variant="outline"
        size="xs"
        onClick={() => reset(role)}
        disabled={status === 'saving' || !defaultsOf(role) || (!details[role]?.is_custom && !draft[role] && !levelDraft[role])}
        title={details[role]?.is_custom || draft[role] || levelDraft[role] ? `Put ${role}'s default ticks in the draft` : 'Already the default access'}
        aria-label={`Reset ${role} to the default access`}
        className="h-[22px]"
        data-testid={`role-reset-${role}`}
      >
        <RotateCcw aria-hidden="true" /> Reset
      </Button>
    </div>
  ) : null);

  const bar = (
    <UnsavedBar
      count={changeCount}
      detail={`${changes.map(accessChangeText).join(' · ')} · affects ${affected} ${affected === 1 ? 'person' : 'people'}`}
      status={status === 'saved' && changeCount === 0 ? 'saved' : status}
      error={applyError}
      savedLabel="Access applied"
    >
      <div className="ml-auto flex w-full items-center justify-end gap-2 sm:w-auto">
        <span className="mr-1 hidden items-center gap-1.5 whitespace-nowrap text-[12px] text-ink-3 lg:inline-flex" data-testid="access-leave-hint">
          <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Leaving this tab will ask first
        </span>
        <Button type="button" variant="ghost" onClick={discard} disabled={status === 'saving'} data-testid="access-discard">Discard</Button>
        <Button type="button" variant="primary" onClick={apply} disabled={status === 'saving' || !changeCount} data-testid="access-apply">
          <Check aria-hidden="true" /> {status === 'error' ? 'Try again' : 'Review & apply'}
        </Button>
      </div>
    </UnsavedBar>
  );

  const preview = shownPreviewRole ? (
    <RolePreview
      ref={previewRef}
      roles={roles}
      role={shownPreviewRole}
      onRoleChange={setPreviewRole}
      savedPermissions={saved}
      permissions={effective}
      savedView={savedView}
      viewOnly={viewOf}
      users={users}
      levels={levelsOn || Object.values(viewOf).some((list) => list.length)}
    />
  ) : null;

  const intro = (
    <p className="text-[13px] text-ink-3">
      Tick what each role can open. Changes stay a draft until you apply them.
      {!canManage ? <span className="ml-1 font-semibold text-status-amber-ink">Only Super Admin can change access.</span> : null}
    </p>
  );

  /* ---------------------------------------------- phones: one accordion per role (28 #9) */
  if (isPhone) {
    return (
      <div className="space-y-3" data-testid="access-accordion">
        {intro}
        <ul className="space-y-2">
          {roles.map((role) => {
            const isSuper = role === SUPER_ADMIN_ROLE;
            const granted = new Set(effective[role] || []);
            const rowDraft = Boolean(draft[role] || levelDraft[role]);
            const viewNow = viewOf[role] || [];
            const viewBefore = savedViewOf(role);
            const open = openRole === role;
            const panelId = `access-role-${role.replace(/\s+/g, '-')}`;
            return (
              <li key={role} className="overflow-hidden rounded-card border border-line bg-surface shadow-card" data-testid={`access-card-${role}`}>
                <button
                  type="button"
                  onClick={() => setOpenRole(open ? '' : role)}
                  aria-expanded={open}
                  aria-controls={panelId}
                  className="flex min-h-[56px] w-full items-center gap-3 px-4 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[14px] font-semibold text-ink">{role}</span>
                    <span className="block text-[12px] text-ink-3">
                      {peopleText(role)} · {isSuper ? `all ${modules.length} modules` : `${granted.size} of ${modules.length} modules`}
                    </span>
                  </span>
                  {isSuper ? <Lock className="h-4 w-4 text-ink-3" aria-label="Fixed" /> : <RoleTag draft={rowDraft} custom={Boolean(details[role]?.is_custom)} canManage={false} role={role} />}
                  <ChevronDown className={cn('h-4 w-4 shrink-0 text-ink-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
                </button>
                {open ? (
                  <div id={panelId} className="border-t border-line-2 px-4 pb-2">
                    {isSuper ? (
                      <p className="py-3 text-[13px] text-brand-ink">All {modules.length} modules — always. Can’t be changed.</p>
                    ) : groups.map((group) => (
                      <div key={group.key} className="pt-3">
                        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{group.label} · {group.modules.length}</p>
                        <ul>
                          {group.modules.map((module) => {
                            const on = granted.has(module.key);
                            const level = accessLevel([...granted], viewNow, module.key);
                            const isDraft = rowDraft && (
                              on !== (saved[role] || []).includes(module.key)
                              || level !== accessLevel(saved[role] || [], viewBefore, module.key)
                            );
                            return (
                              <li key={module.key}>
                                <label className={cn('flex min-h-[44px] items-center gap-3 border-b border-line-2 last:border-b-0', isDraft && 'font-semibold')}>
                                  <span className="min-w-0 flex-1 text-[13.5px] text-ink">
                                    {module.label}
                                    {level === 'view' ? (
                                      <span className="ml-1.5 inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-ink-3" data-testid={`access-view-only-${role}-${module.key}`}>
                                        <Eye className="h-3 w-3" aria-hidden="true" /> view only
                                      </span>
                                    ) : null}
                                    {isDraft ? <span className="ml-1.5 text-[11.5px] font-semibold text-status-amber-ink">changed</span> : null}
                                  </span>
                                  <Switch
                                    checked={on}
                                    onCheckedChange={() => toggle(role, module.key)}
                                    disabled={!canManage || status === 'saving'}
                                    aria-label={cellLabel(role, module, on, isDraft, false, level, true)}
                                    data-testid={`role-access-${role}-${module.key}`}
                                  />
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
        {preview}
        {bar}
      </div>
    );
  }

  /* ---------------------------------------------- desktop / tablet: the matrix (28 #1) */
  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {intro}
          <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
            {levelsOn ? <LevelsLegend /> : <Legend />}
            <Segmented label="Access detail" value={mode} onChange={setMode} options={MODE_OPTIONS} testIdPrefix="access-mode" />
          </div>
        </div>
        {levelsOn ? (
          <p className="-mt-1 flex items-start gap-1.5 text-[12.5px] text-ink-3" data-testid="access-levels-scope">
            <Eye className="mt-[3px] h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {soonNote}
          </p>
        ) : null}

        {/* From 1440px the matrix fits the page (sticky header); narrower windows keep ~47px module columns and
            scroll sideways inside the card with the role column pinned, so no two labels ever touch. */}
        <div className="relative">
        <div ref={matrixScrollRef} className="overflow-x-auto rounded-card border border-line bg-surface shadow-card min-[1440px]:overflow-visible" data-testid="access-matrix-scroll">
          <table className="w-full min-w-[1090px] table-fixed border-collapse min-[1440px]:min-w-0" data-testid="access-matrix">
            <colgroup>
              <col className="w-[236px]" />
              {ordered.map((module) => <col key={module.key} />)}
            </colgroup>
            {/* Header lines are inset shadows, not borders: with border-collapse the table paints borders where
                the rows were, so a sticky header would show the body through a seam between its two rows. */}
            <thead className="min-[1440px]:sticky min-[1440px]:top-[var(--app-header-height)] min-[1440px]:z-10">
              <tr>
                <th
                  scope="col"
                  rowSpan={2}
                  className="sticky left-0 z-[2] rounded-tl-card bg-surface-2 px-4 py-2 text-left align-bottom text-[11.5px] font-semibold uppercase tracking-[0.05em] text-ink-3 shadow-[inset_-1px_0_0_var(--line),inset_0_-1px_0_var(--line)] min-[1440px]:shadow-[inset_0_-1px_0_var(--line)]"
                >
                  Role
                </th>
                {groups.map((group, index) => (
                  <th
                    key={group.key}
                    scope="colgroup"
                    colSpan={group.modules.length}
                    className={cn(
                      'h-7 px-1 text-center text-[10.5px] font-bold uppercase tracking-[0.06em]',
                      index === 0
                        ? 'bg-surface-2 text-ink-2 shadow-[inset_0_-1px_0_var(--line-2)]'
                        : 'rounded-tr-card bg-brand-soft text-brand-ink shadow-[inset_1px_0_0_var(--line),inset_0_-1px_0_var(--line-2)]'
                    )}
                    data-testid={`access-group-${group.key}`}
                  >
                    {group.label} <span className="font-semibold opacity-80">{group.modules.length}</span>
                  </th>
                ))}
              </tr>
              <tr>
                {ordered.map((module) => {
                  const count = counts[module.key] ?? 0;
                  const partial = count < totalRoles;
                  const hint = HINTS[module.key];
                  return (
                    <th
                      key={module.key}
                      scope="col"
                      className={cn(
                        'bg-surface-2 px-0.5 pb-2 pt-2 text-center align-bottom',
                        module.key === firstProjectKey
                          ? 'shadow-[inset_1px_0_0_var(--line),inset_0_-1px_0_var(--line)]'
                          : 'shadow-[inset_0_-1px_0_var(--line)]'
                      )}
                      data-testid={`access-col-${module.key}`}
                    >
                      <span className="block text-[10px] font-bold leading-tight tracking-[-0.03em] text-ink-2 [hyphens:manual] min-[1440px]:text-[10.5px]" title={hint ? `${module.label} — ${hint}` : module.label}>
                        {HEADER_LABELS[module.key] || module.label}
                        {module.key === 'ai' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span tabIndex={0} className="ml-0.5 inline-flex align-[-1px] text-ink-3" aria-label={hint}>
                                <Lock className="h-2.5 w-2.5" aria-hidden="true" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{hint}</TooltipContent>
                          </Tooltip>
                        ) : null}
                      </span>
                      {module.key === 'shop_drawings' ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="mt-0.5 inline-flex items-center whitespace-nowrap text-[9.5px] font-semibold tracking-tight text-ink-3" aria-label="via Tasks" data-testid="access-via-tasks">
                              <Link2 className="mr-px h-2.5 w-2.5" aria-hidden="true" />
                              {/* below 1440px the link icon alone (the tooltip says it): the caption no longer spills into Inventory */}
                              <span className="sr-only min-[1440px]:not-sr-only min-[1440px]:whitespace-nowrap">via Tasks</span>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{hint}</TooltipContent>
                        </Tooltip>
                      ) : null}
                      <span
                        className={cn(
                          'mt-1 block text-[10.5px] tabular-nums',
                          draftModules.has(module.key) ? 'font-bold text-status-amber-ink' : partial ? 'font-bold text-ink' : 'text-ink-3'
                        )}
                        aria-label={`${count} of ${totalRoles} roles${draftModules.has(module.key) ? ', changed in the draft' : ''}`}
                        data-draft={draftModules.has(module.key) || undefined}
                        data-testid={`access-count-${module.key}`}
                      >
                        {count}/{totalRoles}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => {
                const isSuper = role === SUPER_ADMIN_ROLE;
                const granted = new Set(effective[role] || []);
                const before = new Set(saved[role] || []);
                const rowDraft = Boolean(draft[role] || levelDraft[role]);
                const custom = Boolean(details[role]?.is_custom);
                const viewNow = viewOf[role] || [];
                const viewBefore = savedViewOf(role);
                const zeroUsers = userCount(role) === 0;
                return (
                  <tr key={role} className="group border-b border-line-2 last:border-b-0 hover:bg-surface-hover" data-testid={`access-row-${role}`}>
                    <th scope="row" className="sticky left-0 z-[1] h-[46px] bg-surface px-4 text-left align-middle font-normal shadow-[inset_-1px_0_0_var(--line)] group-hover:bg-surface-hover min-[1440px]:shadow-none">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13.5px] font-semibold text-ink">{role}</p>
                          <p className={cn('truncate text-[11.5px] text-ink-3', zeroUsers && 'italic', !isSuper && canManage && 'group-hover:hidden group-focus-within:hidden')}>{peopleText(role)}</p>
                          {isSuper ? null : rowActions(role)}
                        </div>
                        {isSuper ? (
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-line bg-surface px-1.5 text-[11px] font-semibold text-ink-3">
                            <Lock className="h-2.5 w-2.5" aria-hidden="true" /> Fixed
                          </span>
                        ) : (
                          <RoleTag draft={rowDraft} custom={custom} canManage={canManage} role={role} />
                        )}
                      </div>
                    </th>
                    {isSuper ? (
                      <td colSpan={ordered.length} className="bg-brand-soft text-center text-[12.5px] font-semibold text-brand-ink" data-testid="access-super-admin">
                        <span className="inline-flex items-center gap-1.5">
                          <Lock className="h-[13px] w-[13px]" aria-hidden="true" />
                          All {modules.length} modules — always. Can’t be changed.
                        </span>
                      </td>
                    ) : ordered.map((module) => {
                      const on = granted.has(module.key);
                      const readOnly = readOnlyRoles.has(role);
                      const showLevel = levelsOn && (readOnly ? allLevelModules : levelModules).has(module.key);
                      const soon = levelsOn && !readOnly && !levelModules.has(module.key) && allLevelModules.has(module.key);
                      const level = showLevel ? accessLevel([...granted], viewNow, module.key) : null;
                      const levelBefore = showLevel ? accessLevel([...before], viewBefore, module.key) : null;
                      const isDraft = rowDraft && (on !== before.has(module.key) || (showLevel && level !== levelBefore));
                      const viaTasks = module.key === 'shop_drawings' && granted.has('tasks');
                      const label = cellLabel(role, module, on, isDraft, viaTasks, level);
                      return (
                        <td key={module.key} className={cn('p-0 text-center align-middle', module.key === firstProjectKey && 'border-l border-line')}>
                          <button
                            type="button"
                            role="checkbox"
                            aria-checked={on}
                            aria-label={label}
                            title={showLevel && canManage && !readOnly ? `${label} — click: Edit → View only → No access` : soon ? `${label} — View only is not available for ${module.label} yet` : label}
                            disabled={!canManage || status === 'saving'}
                            onClick={() => toggle(role, module.key)}
                            className={cn(
                              'inline-grid h-[46px] w-full place-items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                              canManage && 'hover:bg-brand-soft',
                              !canManage && 'cursor-default'
                            )}
                            data-testid={`role-access-${role}-${module.key}`}
                            data-draft={isDraft || undefined}
                            data-level={level || undefined}
                          >
                            {showLevel ? <LevelMark level={level} draft={isDraft} fixed={readOnlyRoles.has(role)} /> : <AccessMark on={on} draft={isDraft} />}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {moreRight ? (
          <span
            className="pointer-events-none absolute inset-y-px right-px w-12 rounded-r-card bg-gradient-to-l from-[color:var(--surface)] to-transparent"
            aria-hidden="true"
            data-testid="access-matrix-more"
          />
        ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[12.5px] text-ink-3" data-testid="access-footer">
            {totalRoles} roles · {modules.length} modules · {customCount} custom {customCount === 1 ? 'role' : 'roles'}
            {changeCount ? ` · ${changeCount} ${changeCount === 1 ? 'change' : 'changes'} in draft` : ''}
          </p>
          {canManage && resettable.length ? (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1 rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="access-reset-all"
            >
              <RotateCcw className="h-[13px] w-[13px]" aria-hidden="true" /> Reset all roles to defaults
            </button>
          ) : null}
        </div>
        {preview}
        {bar}
      </div>
    </TooltipProvider>
  );
}
